import { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BODY_FIELDS = [
    { key: 'weight', label: 'Peso (kg)', type: 'number', step: '0.01' },
    { key: 'height', label: 'Altura (cm)', type: 'number', step: '0.01' },
    { key: 'body_fat_percentage', label: 'Grasa corporal (%)', type: 'number', step: '0.01' },
    { key: 'muscle_mass_kg', label: 'Masa muscular (kg)', type: 'number', step: '0.01' },
    { key: 'muscle_mass_percentage', label: '% Masa muscular', type: 'number', step: '0.01' },
    { key: 'bone_mass', label: 'Masa ósea (kg)', type: 'number', step: '0.01' },
    { key: 'body_water_percentage', label: '% Agua corporal', type: 'number', step: '0.01' },
    { key: 'visceral_fat', label: 'Grasa visceral', type: 'number', step: '0.01' },
    { key: 'basal_metabolism', label: 'Metabolismo basal (kcal)', type: 'number', step: '1' },
];

const CIRC_FIELDS = [
    { key: 'chest', label: 'Pecho (cm)', type: 'number', step: '0.01' },
    { key: 'waist', label: 'Cintura (cm)', type: 'number', step: '0.01' },
    { key: 'abdomen', label: 'Abdomen (cm)', type: 'number', step: '0.01' },
    { key: 'hips', label: 'Cadera (cm)', type: 'number', step: '0.01' },
    { key: 'neck', label: 'Cuello (cm)', type: 'number', step: '0.01' },
    { key: 'right_arm', label: 'Brazo derecho (cm)', type: 'number', step: '0.01' },
    { key: 'left_arm', label: 'Brazo izquierdo (cm)', type: 'number', step: '0.01' },
    { key: 'right_forearm', label: 'Antebrazo derecho (cm)', type: 'number', step: '0.01' },
    { key: 'left_forearm', label: 'Antebrazo izquierdo (cm)', type: 'number', step: '0.01' },
    { key: 'right_thigh', label: 'Muslo derecho (cm)', type: 'number', step: '0.01' },
    { key: 'left_thigh', label: 'Muslo izquierdo (cm)', type: 'number', step: '0.01' },
    { key: 'right_calf', label: 'Pantorrilla derecha (cm)', type: 'number', step: '0.01' },
    { key: 'left_calf', label: 'Pantorrilla izquierda (cm)', type: 'number', step: '0.01' },
];

function emptyForm() {
    const form = {
        measured_at: new Date().toISOString().slice(0, 16),
        measured_by: '',
        observations: '',
    };
    [...BODY_FIELDS, ...CIRC_FIELDS].forEach(f => { form[f.key] = ''; });
    return form;
}

function measurementToForm(m) {
    if (!m) return emptyForm();
    const form = {
        measured_at: m.measured_at ? m.measured_at.slice(0, 16) : '',
        measured_by: m.measured_by || '',
        observations: m.observations || '',
    };
    [...BODY_FIELDS, ...CIRC_FIELDS].forEach(f => {
        form[f.key] = m[f.key] ?? '';
    });
    return form;
}

export default function MeasurementForm({ open, onOpenChange, measurement, onSave, saving, error }) {
    const [form, setForm] = useState(emptyForm());
    const [activeTab, setActiveTab] = useState('body');

    useEffect(() => {
        setForm(measurementToForm(measurement));
        setActiveTab('body');
    }, [measurement, open]);

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {};
        Object.entries(form).forEach(([k, v]) => {
            if (v === '' || v === null || v === undefined) {
                payload[k] = null;
            } else if (k === 'measured_at' || k === 'measured_by' || k === 'observations') {
                payload[k] = v;
            } else {
                payload[k] = parseFloat(v);
            }
        });
        onSave(payload);
    };

    const renderFields = (fields) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(f => (
                <div key={f.key} className="space-y-2">
                    <Label className="text-white/60 uppercase tracking-wider text-xs">{f.label}</Label>
                    <Input
                        type={f.type}
                        step={f.step}
                        min="0"
                        value={form[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                        className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                    />
                </div>
            ))}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-white uppercase tracking-wide">
                        {measurement ? 'Editar Medición' : 'Nueva Medición'}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha y hora *</Label>
                            <Input
                                type="datetime-local"
                                placeholder="2026-07-15T14:30"
                                value={form.measured_at}
                                onChange={e => handleChange('measured_at', e.target.value)}
                                required
                                className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Medido por</Label>
                            <Input
                                value={form.measured_by}
                                onChange={e => handleChange('measured_by', e.target.value)}
                                placeholder="Nombre del profesional"
                                className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 border-b border-[#3a3a3f] pb-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab('body')}
                            className={`text-xs uppercase tracking-wider px-3 py-1 rounded transition-colors ${activeTab === 'body' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}
                        >
                            Composición
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('circ')}
                            className={`text-xs uppercase tracking-wider px-3 py-1 rounded transition-colors ${activeTab === 'circ' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}
                        >
                            Perímetros
                        </button>
                    </div>

                    {activeTab === 'body' && renderFields(BODY_FIELDS)}
                    {activeTab === 'circ' && renderFields(CIRC_FIELDS)}

                    <div className="space-y-2">
                        <Label className="text-white/60 uppercase tracking-wider text-xs">Observaciones</Label>
                        <textarea
                            className="flex min-h-[60px] w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
                            value={form.observations}
                            onChange={e => handleChange('observations', e.target.value)}
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
